import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { LiveSessionRegistryService } from '../services/live-session-registry.service';
import { catchError, from, of, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const liveSessions = inject(LiveSessionRegistryService);
  const router = inject(Router);

  const isSafeMethod = (request: HttpRequest<unknown>) => ['GET', 'HEAD', 'OPTIONS'].includes(request.method);

  const attachHeaders = (request: HttpRequest<unknown>, csrfToken: string | null) => {
    let headers = request.headers;
    if (csrfToken && !isSafeMethod(request)) {
      headers = headers.set('x-csrf-token', csrfToken);
    }
    return request.clone({ headers, withCredentials: true });
  };

  return next(attachHeaders(req, authService.getCsrfTokenValue())).pipe(
    catchError((error: HttpErrorResponse) => {
      const skipUrls = ['/auth/refresh', '/auth/logout', '/auth/session', '/auth/csrf-token'];
      const isSkipUrl = skipUrls.some(u => req.url.includes(u));

      // 1. JWT Expired -> Refresh Token & Retry
      if (error.status === 401 && !isSkipUrl) {
        // The server already refused this session during a call that is still running. Asking again
        // would only send another refused refresh for every request, in every window, until it ends.
        const alreadyRefused = authService.sessionExpiredDuringCall() ? from(liveSessions.isActiveAnywhere()) : of(false);
        return alreadyRefused.pipe(
          switchMap(holdingSignOut =>
            holdingSignOut
              ? throwError(() => error)
              : authService.refreshToken().pipe(
                  switchMap(() => next(attachHeaders(req, authService.getCsrfTokenValue()))),
                  catchError(refreshError => {
                    // Only the server saying no ends a session. A refresh that timed out or never
                    // reached it says nothing about the session, and the next 401 tries again.
                    const refused = refreshError instanceof HttpErrorResponse && (refreshError.status === 401 || refreshError.status === 403);
                    if (!refused) return throwError(() => refreshError);
                    // Logging out navigates to /login, which tears down a call screen and its socket, and
                    // the socket only authenticates at the handshake, so it would have kept working. During
                    // a call this request just fails; the session ends when the call does
                    // (LiveSessionRegistryService).
                    return from(liveSessions.isActiveAnywhere()).pipe(
                      switchMap(callRunning => {
                        if (callRunning) {
                          authService.markSessionExpiredDuringCall();
                        } else {
                          authService.logout();
                          router.navigate(['/login']);
                        }
                        return throwError(() => refreshError);
                      })
                    );
                  })
                )
          )
        );
      }

      // 2. CSRF Token Invalid/Missing -> Auto-Retry (Circuit Breaker Enabled)
      const errString = JSON.stringify(error.error || '').toLowerCase();
      // A blob request gets its error body as an unread Blob, so its 403 is retried once as a CSRF error.
      // Only a method that carries a CSRF token can fail on one; a GET's 403 is a real refusal.
      const isBlobError = !isSafeMethod(req) && req.responseType === 'blob' && error.error instanceof Blob;
      const isCsrfError = error.status === 403 && (isBlobError || /\b(csrf|xsrf)\b/i.test(errString));
      const isCsrfRetry = req.headers.has('x-csrf-retry');

      if (isCsrfError && !isCsrfRetry) {
        return authService.fetchCsrfToken().pipe(
          switchMap(res => {
            const retryHeaders = req.headers.set('x-csrf-token', res.token).set('x-csrf-retry', 'true');
            const retryReq = req.clone({ headers: retryHeaders, withCredentials: true });
            return next(retryReq);
          }),
          catchError(csrfError => throwError(() => csrfError))
        );
      }

      return throwError(() => error);
    })
  );
};
